import type { ModulesAndAgendasState } from '../index';
import { findSectionIndex, findTabIndex } from 'utils/section/tabSection';
import { findItemIndexByCoordinate, recalcSectionItems } from 'utils/section/items';
import { createPosString, getSpan, getStartPos, checkAreaOverlap } from 'utils/section/gridUtils';

interface Payload {
  draggedSectionName: string;
  droppedSectionName: string;
  from: { x: string; y: string };
  to: { x: string; y: string };
  currentTab: number;
  currentSubTab: number;
  startTab?: number;
  startSubTab?: number;
}

export const reorderSectionItem = (state: ModulesAndAgendasState, { payload }: { payload: Payload }) => {
  const { draggedSectionName, droppedSectionName, currentTab, from, to, currentSubTab, startTab, startSubTab } = payload;

  const fromX = getStartPos(from.x);
  const fromY = getStartPos(from.y);

  const toX = getStartPos(to.x);
  const toY = getStartPos(to.y);

  const tabSections = state.tabSections;
  const currentTabIndex = findTabIndex(tabSections, currentTab);
  if (currentTabIndex === -1) return;

  const startTabIndex = findTabIndex(tabSections, startTab ?? currentTab);
  if (startTabIndex === -1) return;

  const getItemsList = (tabIndex: number, sectionName: string, subTab: number) => {
    const sectionIndex = findSectionIndex(tabSections[tabIndex].sections, sectionName);

    if (sectionIndex === -1) return null;

    const section = tabSections[tabIndex].sections[sectionIndex];

    let subTabIndex = findTabIndex(section.formItemTabs, subTab);

    if (subTabIndex === -1 && section.formItemTabs.length === 1) {
      subTabIndex = 0;
    }

    if (subTabIndex === -1) return null;

    return {
      items: section.formItemTabs[subTabIndex].items,
      section,
      subTabIndex,
      sectionIndex,
      tabIndex,
      gridSizeX: section.gridSizeX,
      gridSizeY: section.gridSizeY,
    };
  };

  const sourceData = getItemsList(startTabIndex, draggedSectionName, startSubTab ?? currentSubTab);
  const targetData = getItemsList(currentTabIndex, droppedSectionName, currentSubTab);

  if (!sourceData || !targetData) return;

  const sourceItems = sourceData.items;
  const targetItems = targetData.items;

  let fromIndex = sourceItems.findIndex((i) => getStartPos(i.x) === fromX && getStartPos(i.y) === fromY);

  if (fromIndex === -1) {
    fromIndex = findItemIndexByCoordinate(sourceItems, fromX, fromY);
  }

  if (fromIndex === -1) {
    return;
  }

  const sourceItem = sourceItems[fromIndex];
  const sourceSpanX = getSpan(sourceItem.x);
  const sourceSpanY = getSpan(sourceItem.y);

  const maxX = targetData.gridSizeX - sourceSpanX;
  const maxY = targetData.gridSizeY - sourceSpanY;

  if (toX < 0 || toY < 0 || toX > maxX || toY > maxY) {
    return;
  }

  let toIndex = findItemIndexByCoordinate(targetItems, toX, toY);

  if (toIndex === -1) {
    toIndex = targetItems.findIndex((i) => getStartPos(i.x) === toX && getStartPos(i.y) === toY);
  }

  const sourceAndTargetAreDifferentSections =
    draggedSectionName !== droppedSectionName || startTabIndex !== currentTabIndex || (startSubTab !== undefined && startSubTab !== currentSubTab);

  if (toIndex === fromIndex && !sourceAndTargetAreDifferentSections) {
    return;
  }

  if (toIndex === -1) {
    return;
  }

  const targetItem = targetItems[toIndex];

  if (targetItem.isEmpty) {
    const ignoredId = sourceItem.data?.id ? String(sourceItem.data.id) : '';

    const overlapCheck = checkAreaOverlap(
      targetItems,
      { x: toX, y: toY, w: sourceSpanX, h: sourceSpanY },
      { x: targetData.gridSizeX, y: targetData.gridSizeY },
      ignoredId
    );

    if (overlapCheck.isCollision) return;

    const targetSpanX = getSpan(targetItem.x);
    const targetSpanY = getSpan(targetItem.y);

    const newSourceX = createPosString(toX, sourceSpanX);
    const newSourceY = createPosString(toY, sourceSpanY);
    const newTargetX = createPosString(fromX, targetSpanX);
    const newTargetY = createPosString(fromY, targetSpanY);

    sourceItem.x = newSourceX;
    sourceItem.y = newSourceY;
    if (sourceItem.data) {
      sourceItem.data.x = newSourceX;
      sourceItem.data.y = newSourceY;
    }

    targetItem.x = newTargetX;
    targetItem.y = newTargetY;

    if (sourceAndTargetAreDifferentSections) {
      sourceItems[fromIndex] = targetItem;
      targetItems[toIndex] = sourceItem;
    } else {
      sourceItems[fromIndex] = targetItem;
      sourceItems[toIndex] = sourceItem;
    }
  } else {
    const tSX = getSpan(targetItem.x);
    const tSY = getSpan(targetItem.y);

    if (sourceSpanX !== tSX || sourceSpanY !== tSY) return;

    const tempContent = sourceItem.data;
    const tempIsEmpty = sourceItem.isEmpty;

    sourceItem.data = targetItem.data;
    sourceItem.isEmpty = targetItem.isEmpty;

    targetItem.data = tempContent;
    targetItem.isEmpty = tempIsEmpty;

    if (sourceItem.data) {
      sourceItem.data.x = sourceItem.x;
      sourceItem.data.y = sourceItem.y;
    }
    if (targetItem.data) {
      targetItem.data.x = targetItem.x;
      targetItem.data.y = targetItem.y;
    }
  }

  state.tabSections[sourceData.tabIndex].sections[sourceData.sectionIndex].formItemTabs[sourceData.subTabIndex].items = sourceItems;
  if (sourceAndTargetAreDifferentSections) {
    state.tabSections[targetData.tabIndex].sections[targetData.sectionIndex].formItemTabs[targetData.subTabIndex].items = targetItems;
  }

  sourceData.section.items = recalcSectionItems(sourceData.section.formItemTabs);
  if (sourceAndTargetAreDifferentSections) {
    targetData.section.items = recalcSectionItems(targetData.section.formItemTabs);
  }
};
